import React, { type AriaAttributes, type MutableRefObject } from 'react';

import { useResizeObserver, useScrollListener } from '@gitbook/util-react-hooks';
import { assertNever } from '@gitbook/util-ts';

export type ScrollFaderController = ReturnType<typeof useScrollFaderController>;

export function useScrollFaderController<ElType extends HTMLElement = HTMLDivElement>(
    orientation: AriaAttributes['aria-orientation'] = 'horizontal',
    ref?: MutableRefObject<ElType | null>
) {
    const defaultRef = React.useRef<ElType>(null);
    const scrollableRef = ref || defaultRef;

    const [[start, end], setIndicator] = React.useState([false, false]);

    // Update the scroll indicator position
    const updateScrollIndicator = React.useCallback(() => {
        const element = scrollableRef.current;
        if (!element) {
            return;
        }
        setIndicator(
            orientation === 'horizontal'
                ? [
                      element.scrollLeft > 0,
                      // testing scroll position this way because of rounding - see https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollHeight#determine_if_an_element_has_been_totally_scrolled
                      Math.abs(element.scrollWidth - element.offsetWidth - element.scrollLeft) > 1,
                  ]
                : [
                      element.scrollTop > 0,
                      // testing scroll position this way because of rounding - see https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollHeight#determine_if_an_element_has_been_totally_scrolled
                      Math.abs(element.scrollHeight - element.offsetHeight - element.scrollTop) > 1,
                  ]
        );
    }, [orientation, scrollableRef]);

    // Listen for resize on container
    const resizeRef = useResizeObserver(() => updateScrollIndicator());

    // Update on mount
    React.useLayoutEffect(() => updateScrollIndicator(), [updateScrollIndicator]);

    // Update on scroll
    useScrollListener(() => updateScrollIndicator(), scrollableRef);

    return {
        ref: scrollableRef,
        resizeRef,
        update: updateScrollIndicator,
        ...getControls({ orientation, start, end, scrollableRef }),
        start,
        end,
        orientation,
    };
}

function getControls<ElType extends HTMLElement = HTMLDivElement>(props: {
    orientation: 'horizontal' | 'vertical';
    start: boolean;
    end: boolean;
    scrollableRef: MutableRefObject<ElType | null>;
}) {
    const { orientation, start, end, scrollableRef } = props;
    return {
        next: end
            ? () => {
                  const element = scrollableRef.current;
                  if (!element) {
                      return;
                  }
                  switch (orientation) {
                      case 'horizontal':
                          scrollRight(element);
                          break;
                      case 'vertical':
                          scrollDown(element);
                          break;
                      default:
                          assertNever(orientation);
                  }
              }
            : undefined,
        previous: start
            ? () => {
                  const element = scrollableRef.current;
                  if (!element) {
                      return;
                  }
                  switch (orientation) {
                      case 'horizontal':
                          scrollLeft(element);
                          break;
                      case 'vertical':
                          scrollUp(element);
                          break;
                      default:
                          assertNever(orientation);
                  }
              }
            : undefined,
    };
}

function scrollLeft(element) {
    const newScrollPosition = element.scrollLeft - element.clientWidth;
    element.scrollTo({
        left: newScrollPosition,
        behavior: 'smooth',
    });
}

function scrollRight(element) {
    const newScrollPosition = element.scrollLeft + element.clientWidth;
    element.scrollTo({
        left: newScrollPosition,
        behavior: 'smooth',
    });
}

function scrollUp(element) {
    const newScrollPosition = element.scrollTop - element.clientHeight;
    element.scrollTo({
        top: newScrollPosition,
        behavior: 'smooth',
    });
}

function scrollDown(element) {
    const newScrollPosition = element.scrollTop + element.clientHeight;
    element.scrollTo({
        top: newScrollPosition,
        behavior: 'smooth',
    });
}
